import { Box } from "@chakra-ui/react";
import type { SelectProps } from "@src/components/ui-kit/Select/Select";
import { useVirtualizer } from "@tanstack/react-virtual";
import { chakraComponents } from "chakra-react-select";
import { Children, type ReactElement, useEffect, useMemo, useRef } from "react";

type VirtualizedMenuListProps = {
  readonly menuProps: any;
  readonly topExtraContent?: SelectProps["topExtraContent"];
  readonly bottomExtraContent?: SelectProps["bottomExtraContent"];
  readonly optionHeight?: number;
  readonly overscan?: number;
};

const DEFAULT_OPTION_HEIGHT = 40;
const DEFAULT_OVERSCAN = 8;

export const VirtualizedMenuList = ({
  menuProps,
  topExtraContent,
  bottomExtraContent,
  optionHeight = DEFAULT_OPTION_HEIGHT,
  overscan = DEFAULT_OVERSCAN,
}: VirtualizedMenuListProps) => {
  const listRef = useRef<HTMLDivElement | null>(null);
  const originalInnerRef = menuProps.innerRef;
  const children = useMemo(
    () => Children.toArray(menuProps.children) as ReactElement[],
    [menuProps.children],
  );

  const virtualizer = useVirtualizer({
    getScrollElement: () => listRef.current,
    count: children.length,
    estimateSize: () => optionHeight,
    overscan,
  });

  useEffect(() => {
    const focusedOptionValue = menuProps.focusedOption?.value;
    if (!focusedOptionValue) return;

    const focusedIndex = children.findIndex(
      (child) => child?.props?.data?.value === focusedOptionValue,
    );
    if (focusedIndex < 0) return;

    virtualizer.scrollToIndex(focusedIndex, { align: "auto" });
  }, [children, menuProps.focusedOption?.value, virtualizer]);

  const handleMenuListRef = (node: HTMLDivElement | null) => {
    listRef.current = node;

    if (!originalInnerRef) return;

    if (typeof originalInnerRef === "function") {
      originalInnerRef(node);
      return;
    }

    if ("current" in originalInnerRef) {
      originalInnerRef.current = node;
    }
  };

  return (
    <Box
      sx={{
        ">div": { border: "none" },
      }}
      overflow="hidden"
      minW="fit-content"
      borderRadius="md"
    >
      <chakraComponents.MenuList
        {...menuProps}
        innerRef={handleMenuListRef}
        overflowY="auto"
        maxH={`${menuProps.maxHeight}px`}
      >
        {topExtraContent}
        <Box
          pos="relative"
          overflow="hidden"
          w="full"
          h={`${virtualizer.getTotalSize()}px`}
        >
          {virtualizer.getVirtualItems().map((virtualItem) => (
            <Box
              key={virtualItem.key}
              pos="absolute"
              top="0"
              left="0"
              w="full"
              transform={`translateY(${virtualItem.start}px)`}
            >
              {children[virtualItem.index]}
            </Box>
          ))}
        </Box>
      </chakraComponents.MenuList>
      {bottomExtraContent}
    </Box>
  );
};
